import { Subscribe } from '@react-rxjs/core';
import clsx from 'clsx';
import { FC } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';
import LocationIcon from '../../assets/icons/location.svg?react';
import RestingIcon from '../../assets/icons/tacho_rest.svg?react';
import WorkingIcon from '../../assets/icons/tacho_working.svg?react';
import WarningIcon from '../../assets/icons/warning.svg?react';
import { Button, Link } from '../../components/Button';
import { Card } from '../../components/Card';
import { CardTitle } from '../../components/CardTitle';
import { DriverGreeting } from '../../components/DriverGreeting';
import { Spinner } from '../../components/Spinner';
import { TripTypeSelector } from '../../components/TripTypeSelector';
import { VehiclePlate } from '../../components/VehiclePlate';
import { AssignationSource, useDriver } from '../../services/driver';
import { useIsFeatureEnabled } from '../../services/features';
import { useQuestionnaires } from '../../services/inspection';
import {
  changeDriverStatus,
  useCurrentDriverStatus,
  useDriverStatusChange,
} from '../../services/logbook';
import {
  refreshPrivateMileage,
  usePrivateMileage,
} from '../../services/privateMileage';
import { setTripType, useTripType } from '../../services/tripType';
import { unasignVehicle, useVehicle } from '../../services/vehicle';
import { isAndroid, isApple } from '../../utils/isMobile';
import { InspectionNotification } from '../Inspection/InspectionNotification';
import styles from './Main.module.css';
import { currentStatusfallbackRender } from './fallbacks';
import { HeaderSlot } from '../../components/Shell';

interface AssignationProps extends AssignationButtonProps {}

function getGeoUrl({
  latitude,
  longitude,
}: {
  latitude: number;
  longitude: number;
}): string {
  if (isAndroid()) {
    return `geo:0,0?q=${latitude},${longitude}`;
  }

  if (isApple()) {
    return `https://maps.apple.com/?ll=${latitude},${longitude}&q=${latitude},${longitude}`;
  }

  return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
}

export const Assignation: FC<AssignationProps> = (props) => {
  const { result, running, error } = useVehicle();

  if (error) {
    return null;
  }

  const coord = result?.latitude
    ? {
        latitude: result.latitude,
        longitude: result.longitude,
      }
    : undefined;

  return (
    <Card className={styles.assignation}>
      <div className={styles.titleRow}>
        <CardTitle>
          <FormattedMessage id="Vehicle.Assign.Item" />
        </CardTitle>
        {result?.name ? (
          <VehiclePlate active>{result?.name}</VehiclePlate>
        ) : (
          <div className={styles.noAssignation}>—</div>
        )}
      </div>
      <div className={styles.buttons}>
        {running ? (
          <Spinner />
        ) : (
          <>
            {coord && (
              <Link
                className={styles.smlBtn}
                variant="outlined"
                href={getGeoUrl(coord)}
                target="_blank"
              >
                <span className={styles.location}>
                  <FormattedMessage id="Location" />
                </span>
                <LocationIcon className={styles.locationIcon} />
              </Link>
            )}
            <AssignationButton {...props} />
          </>
        )}
      </div>
    </Card>
  );
};

interface AssignationButtonProps {
  onAssign: () => void;
}

export const AssignationButton: FC<AssignationButtonProps> = ({ onAssign }) => {
  const {
    result: resultDriver,
    error: errorDriver,
    running: runningDriver,
  } = useDriver();
  const { result: resultVehicle } = useVehicle();

  if (runningDriver) {
    return <Spinner />;
  }

  if (errorDriver || !resultDriver) {
    return null;
  }

  const { assignation } = resultDriver;

  if (resultVehicle && (!assignation || assignation.source === 'MANUAL')) {
    return (
      <Button
        onClick={() => unasignVehicle(resultVehicle.id)}
        className={styles.assignBtn}
      >
        <FormattedMessage id="Vehicle.Unassign" />
      </Button>
    );
  }

  if (resultVehicle && assignation) {
    return <AssignationStatus status={assignation.source} />;
  }

  return (
    <Button onClick={onAssign} className={styles.assignBtn}>
      <FormattedMessage id="Vehicle.Assign" />
    </Button>
  );
};

interface AssignationStatusProps {
  status: AssignationSource;
}

const assignationMap: Record<AssignationSource, string> = {
  IBUTTON: 'iButton',
  MANUAL: 'Manual',
  TACHO: 'Tachograph',
  WIRELESS: 'Wireless',
  UNKNOWN: 'Unknown',
};

export const AssignationStatus: FC<AssignationStatusProps> = ({ status }) => (
  <div className={styles.status}>{assignationMap[status]}</div>
);

interface TripTypeProps {}

export const DefaultTripType: FC<TripTypeProps> = () => {
  const { result } = useTripType();

  if (result === null) {
    return null;
  }

  return (
    <Card>
      <CardTitle>
        <FormattedMessage id="Trip.Type" />
      </CardTitle>
      {result === '' ? (
        <FormattedMessage tagName="p" id="Vehicle.Assign.Automatically" />
      ) : (
        <TripTypeSelector tripType={result} onChange={setTripType} />
      )}
    </Card>
  );
};

export const InspectionNotifications: FC = () => {
  const questionnaires = useQuestionnaires();
  return (
    <HeaderSlot slot="content">
      {questionnaires.map((item) => (
        <InspectionNotification
          key={item.id}
          id={item.id}
          title={item.payload.questionnaire.title}
        />
      ))}
      <DriverGreeting />
    </HeaderSlot>
  );
};

export const PrivateMileage: FC = () => {
  const { running, error, result } = usePrivateMileage();

  if (error) {
    return <PrivateMileageError />;
  }

  return (
    <Card>
      <CardTitle>
        <FormattedMessage id="PrivateMileage.Title" />
      </CardTitle>
      <div className={styles.privateMileage}>
        {running ? <Spinner /> : `${result} km`}
      </div>
      <div className={styles.privateMileageFooter}>
        <FormattedMessage id="PrivateMileage.UpdateMessage" />
      </div>
    </Card>
  );
};

export const PrivateMileageError: FC = () => (
  <Card className={styles.privateMileageError}>
    <div>
      <WarningIcon className={styles.alertIcon} />
      <FormattedMessage tagName="span" id="PrivateMileage.ErrorMessage" />
    </div>
    <Button variant="outlined" compact onClick={refreshPrivateMileage}>
      <FormattedMessage id="Retry" />
    </Button>
  </Card>
);

const CurrentStatus: FC = () => {
  const status = useCurrentDriverStatus();
  const { running } = useDriverStatusChange();

  return (
    <Card>
      <CardTitle>
        <FormattedMessage id="CurrentStatus" />
      </CardTitle>
      <div className={styles.currentStatus}>
        <div
          className={clsx(
            styles.currentStatusBadge,
            status === 'WORKING' ? styles.working : styles.resting
          )}
        >
          {status === 'WORKING' ? <WorkingIcon /> : <RestingIcon />}
          <FormattedMessage id={status === 'WORKING' ? 'Working' : 'Resting'} />
        </div>

        <Button
          disabled={running}
          onClick={() =>
            changeDriverStatus(status === 'WORKING' ? 'RESTING' : 'WORKING')
          }
        >
          <FormattedMessage
            id={status === 'WORKING' ? 'SwitchToResting' : 'SwitchToWorking'}
          />
        </Button>
      </div>
    </Card>
  );
};

export const CurrentStatusFeature: FC = () => {
  const enabled = useIsFeatureEnabled('LOGBOOK');

  if (!enabled) {
    return null;
  }

  return (
    <ErrorBoundary fallbackRender={currentStatusfallbackRender}>
      <Subscribe fallback={null}>
        <CurrentStatus />
      </Subscribe>
    </ErrorBoundary>
  );
};
